import { STATEMENT_REFUSAL } from './statementJobView'

/**
 * Why the queue drawer offers no Download for a run it can open — spec 167.
 *
 * ## The defect, as measured on QA (vladdemo, 2026-10-12)
 *
 * A fresh Aged A/R saved run (report 21: 4f5cb651, cc6d48cc, both scheduled, 69 rows)
 * opened to a drawer with "Open report" and a kebab holding only "Copy job ID" and
 * "Dismiss from queue". `/reports/jobs/:id/result` answered 200 (149,729 bytes) and the
 * report page rendered the run. So the run is readable; the drawer just would not
 * build a file from it, and nothing on screen said why.
 *
 * The mechanism, read from the code and the live payload rather than the ticket:
 *
 *   - Download from the drawer is offered for a generic run with a `processId`, or a
 *     non-generic run whose stored blob adapts into a table (`viewFromTabularResult`)
 *     or a statement (`statementJobView`). A/R stores its OWN grouped shape —
 *     `{ data: { rows, columnSettings, partialData } }`, no `headers` — so the adapter
 *     answers null, by design, and the drawer recorded `unsupported`.
 *   - It DID have a sentence for that, "No file download for this report — use Open
 *     report, then Download". But it was a DISABLED kebab item: invisible until the
 *     menu is opened, painted `text-gray-300` (#d1d5dc in this build, 1.472:1 on white), and not
 *     a `menuitem`, so a reader scanning the menu — and the live audit — never met it.
 *     And it said what to do, not why.
 *
 * ## Why the drawer still does not build A/R's file
 *
 * Not because no path exists — because the only correct one belongs to the page. The
 * A/R page's export is built from its VIEW, not its payload: it collapses CB's
 * one-row-per-invoice result into one row per matter by summing (CC-49597), drops
 * detail columns unless "Show details" is on, and applies the reader's hide-zero,
 * inactive-matter and practice-area display filters and their Show $ choice
 * (`ARReport.jsx` `buildClientView`). A file built here from the raw rows would carry
 * different rows and different totals from the page's own Download for the same run —
 * two exports disagreeing on A/R money. So the drawer refuses, and now says so where it
 * can be read, with the reason.
 *
 * Every note is a plain sentence the drawer renders in its body (`job-download-note`),
 * not a disabled menu item.
 */

/** The stored result is a real payload, but not a flat table this drawer can write. */
export const NOT_A_TABLE = 'not-a-table'

export const DOWNLOAD_NOTES = Object.freeze({
  teammate: 'A teammate ran this — only they can open or download the saved result.',
  preparing: 'Preparing download…',
  unreadable: 'Download unavailable — the saved result could not be read.',
  [NOT_A_TABLE]:
    'No download from here: this report’s saved result isn’t a plain table, so only the report’s own page can build its file. Open report, then use Download there.',
  [STATEMENT_REFUSAL.NO_SAVED_PARAMS]:
    'No download from here: the filters this statement ran with couldn’t be read, and its columns depend on them.',
  [STATEMENT_REFUSAL.CATEGORY_PNL]:
    'No download from here: a P&L by category is laid out by its own page. Open report, then use Download there.',
})

/**
 * The note to show, or null when Download is offered or the run has nothing to open.
 *
 * @param {object} o
 * @param {boolean} o.isOwner   the reader ran it
 * @param {boolean} [o.adminAccess] spec 169: cb-api says this reader may open another
 *   user's private-schedule run as a CB administrator (strictly `true`)
 * @param {boolean} o.canView   ready, openable by this reader, and has a report to open
 * @param {boolean} o.canDownload a Download item is offered
 * @param {{status: string, reason?: string}} o.stored the drawer's stored-result read
 */
export const downloadNoteFor = ({ isOwner, adminAccess = false, canView, canDownload, stored }) => {
  // An empty footer on a colleague's run would read as a regression: say whose it is.
  // Spec 169: unless this reader may open it as an administrator — then the usual notes.
  if (!isOwner && adminAccess !== true) return DOWNLOAD_NOTES.teammate
  if (canDownload || !canView) return null
  if (stored?.status === 'loading') return DOWNLOAD_NOTES.preparing
  if (stored?.status === 'error') return DOWNLOAD_NOTES.unreadable
  if (stored?.status === 'unsupported') return DOWNLOAD_NOTES[stored.reason] || DOWNLOAD_NOTES[NOT_A_TABLE]
  return null
}
